/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */


import {
  AfterContentInit,
  Component,
  Directive,
  ElementRef,
  Inject,
  Input,
  NgZone,
  OnDestroy,
  Optional,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import {MdInkBar} from '../ink-bar';
import {MdRipple} from '../../core/ripple/index';
import {ViewportRuler} from '../../core/overlay/position/viewport-ruler';
import {Dir, MD_RIPPLE_GLOBAL_OPTIONS, Platform, RippleGlobalOptions} from '../../core';
import {Observable} from 'rxjs/Observable';
import'rxjs/add/operator/auditTime';
import'rxjs/add/operator/takeUntil';
import'rxjs/add/observable/of';
import'rxjs/add/observable/merge';
import {Subject} from 'rxjs/Subject';
/**
 * Navigation component matching the styles of the tab group header.
 * Provides anchored navigation with animated ink bar.
 */
export class MdTabNav implements AfterContentInit, OnDestroy {
/**
 * Subject that emits when the component has been destroyed.
 */
private _onDestroy = new Subject<void>();

  _activeLinkChanged: boolean;
  _activeLinkElement: ElementRef;

   _inkBar: MdInkBar;
/**
 * @param {?} _dir
 * @param {?} _ngZone
 */
constructor(
private _dir: Dir,
private _ngZone: NgZone) { }
/**
 * Notifies the component that the active link has been changed.
 * @param {?} element
 * @return {?}
 */
updateActiveLink(element: ElementRef) {
    this._activeLinkChanged = this._activeLinkElement != element;
    this._activeLinkElement = element;
  }
/**
 * @return {?}
 */
ngAfterContentInit(): void {
    this._ngZone.runOutsideAngular(() => {
      let /** @type {?} */ dirChange = this._dir ? this._dir.dirChange : Observable.of(null);
      let /** @type {?} */ resize = typeof window !== 'undefined' ?
          Observable.fromEvent(window, 'resize').auditTime(10) :
          Observable.of(null);

      return Observable.merge(dirChange, resize)
          .takeUntil(this._onDestroy)
          .subscribe(() => this._alignInkBar());
    });
  }
/**
 * Checks if the active link has been changed and, if so, will update the ink bar.
 * @return {?}
 */
ngAfterContentChecked(): void {
    if (this._activeLinkChanged) {
      this._alignInkBar();
      this._activeLinkChanged = false;
    }
  }
/**
 * @return {?}
 */
ngOnDestroy() {
    this._onDestroy.next();
  }
/**
 * Aligns the ink bar to the active link.
 * @return {?}
 */
_alignInkBar(): void {
    if (this._activeLinkElement) {
      this._inkBar.alignToElement(this._activeLinkElement.nativeElement);
    }
  }
static decorators: DecoratorInvocation[] = [
{ type: Component, args: [{
  moduleId: module.id,
  selector: '[md-tab-nav-bar], [mat-tab-nav-bar]',
  templateUrl: 'tab-nav-bar.html',
  styleUrls: ['tab-nav-bar.css'],
  host: {'class': 'mat-tab-nav-bar'},
  encapsulation: ViewEncapsulation.None,
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: Dir, decorators: [{ type: Optional }, ]},
{type: NgZone, },
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'_inkBar': [{ type: ViewChild, args: [MdInkBar, ] },],
};
}

function MdTabNav_tsickle_Closure_declarations() {
/** @type {?} */
MdTabNav.decorators;
/**
 * @nocollapse
 * @type {?}
 */
MdTabNav.ctorParameters;
/** @type {?} */
MdTabNav.propDecorators;
/**
 * Subject that emits when the component has been destroyed.
 * @type {?}
 */
MdTabNav.prototype._onDestroy;
/** @type {?} */
MdTabNav.prototype._activeLinkChanged;
/** @type {?} */
MdTabNav.prototype._activeLinkElement;
/** @type {?} */
MdTabNav.prototype._inkBar;
/** @type {?} */
MdTabNav.prototype._dir;
/** @type {?} */
MdTabNav.prototype._ngZone;
}

/**
 * Link inside of a `md-tab-nav-bar`.
 */
export class MdTabLink {
private _isActive: boolean = false;
/**
 * Whether the link is active.
 * @return {?}
 */
get active(): boolean { return this._isActive; }
/**
 * @param {?} value
 * @return {?}
 */
set active(value: boolean) {
    this._isActive = value;
    if (value) {
      this._mdTabNavBar.updateActiveLink(this._elementRef);
    }
  }
/**
 * @param {?} _mdTabNavBar
 * @param {?} _elementRef
 */
constructor(private _mdTabNavBar: MdTabNav,
private _elementRef: ElementRef) {}
static decorators: DecoratorInvocation[] = [
{ type: Directive, args: [{
  selector: '[md-tab-link], [mat-tab-link], [mdTabLink], [matTabLink]',
  host: {'class': 'mat-tab-link'}
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: MdTabNav, },
{type: ElementRef, },
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'active': [{ type: Input },],
};
}

function MdTabLink_tsickle_Closure_declarations() {
/** @type {?} */
MdTabLink.decorators;
/**
 * @nocollapse
 * @type {?}
 */
MdTabLink.ctorParameters;
/** @type {?} */
MdTabLink.propDecorators;
/** @type {?} */
MdTabLink.prototype._isActive;
/** @type {?} */
MdTabLink.prototype._mdTabNavBar;
/** @type {?} */
MdTabLink.prototype._elementRef;
}

/**
 * Simple directive that extends the ripple and matches the selector of the MdTabLink. This
 * adds the ripple behavior to nav bar labels.
 */
export class MdTabLinkRipple extends MdRipple {
/**
 * @param {?} elementRef
 * @param {?} ngZone
 * @param {?} ruler
 * @param {?} platform
 * @param {?} globalOptions
 */
constructor(
      elementRef: ElementRef,
      ngZone: NgZone,
      ruler: ViewportRuler,
      platform: Platform,
        globalOptions: RippleGlobalOptions) {
    super(elementRef, ngZone, ruler, platform, globalOptions);
  }
static decorators: DecoratorInvocation[] = [
{ type: Directive, args: [{
  selector: '[md-tab-link], [mat-tab-link], [mdTabLink], [matTabLink]',
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: ElementRef, },
{type: NgZone, },
{type: ViewportRuler, },
{type: Platform, },
{type: undefined, decorators: [{ type: Optional }, { type: Inject, args: [MD_RIPPLE_GLOBAL_OPTIONS, ] }, ]},
];
}

function MdTabLinkRipple_tsickle_Closure_declarations() {
/** @type {?} */
MdTabLinkRipple.decorators;
/**
 * @nocollapse
 * @type {?}
 */
MdTabLinkRipple.ctorParameters;
}


interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
